import { useEffect } from 'react';
import { trackEvent } from '@99designs/tracking';
import { ContestFixedPriceProjectLayout } from '../components/ContestFixedPriceProjectLayout';
import { ContestLayout } from '../components/ContestLayout';
import { FixedPriceProjectLayout } from '../components/FixedPriceProjectLayout';
import { useRelatedProductsConfig } from '../util/useRelatedProductsConfig';
import type { Design } from '@99designs/feature-design-inspiration';
import type { CurrencyCode } from '@99designs/i18n';
import type { ZendeskLanguage } from '@99designs/routing';
import type {
    ContestProduct,
    FixedPriceProjectProduct,
    ProductGroup,
    ProductGroupKey,
    QuestionAndAnswer,
    RelatedProduct,
} from '../types';

export type ProductDetailsContainerProps = {
    productGroup: ProductGroup;
    products: ContestProduct[] | FixedPriceProjectProduct[];
    designInspiration: Design[] | null;
    productGroupKey: ProductGroupKey;
    relatedProducts: RelatedProduct[] | null;
    faqs: QuestionAndAnswer[];
    currency: CurrencyCode;
    lang: ZendeskLanguage;
};

export function ProductDetailsContainer({
    productGroup,
    products,
    designInspiration,
    productGroupKey,
    relatedProducts,
    faqs,
    currency,
    lang,
}: ProductDetailsContainerProps) {
    useEffect(() => {
        trackEvent('Viewed Product Details Page', {
            type: productGroup.attributes.type,
            productGroupKey: productGroupKey,
        });
    }, [productGroup.attributes.type, productGroupKey]);

    const relatedProductsFromConfig: RelatedProduct[] = useRelatedProductsConfig(
        relatedProducts,
        currency
    );

    const inspiration =
        designInspiration != null && designInspiration.length > 0
            ? {
                  designOutcome: productGroup.attributes.designOutcome,
                  categoryKeys: [productGroup.designInspirationCategoryKey],
                  designs: designInspiration,
              }
            : undefined;

    switch (productGroup.attributes.type) {
        case 'PRODUCT_GROUP_CONTEST':
            return (
                <ContestLayout
                    productGroup={productGroup}
                    products={products as ContestProduct[]}
                    inspiration={inspiration}
                    relatedProducts={relatedProductsFromConfig}
                    faqs={faqs}
                    lang={lang}
                />
            );
        case 'PRODUCT_GROUP_FIXED_PRICE':
            return (
                <FixedPriceProjectLayout
                    productGroup={productGroup}
                    products={products as FixedPriceProjectProduct[]}
                    inspiration={inspiration}
                    relatedProducts={relatedProductsFromConfig}
                    faqs={faqs}
                    lang={lang}
                />
            );
        case 'PRODUCT_GROUP_CONTEST_AND_FIXED_PRICE':
            return (
                <ContestFixedPriceProjectLayout
                    productGroup={productGroup}
                    products={products}
                    inspiration={inspiration}
                    relatedProducts={relatedProductsFromConfig}
                    faqs={faqs}
                    lang={lang}
                />
            );
        default:
            return null;
    }
}
